import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Me } from '../lib/api';
import { navigate, useHashRoute } from '../lib/router';
import { initials, titleCase } from '../lib/format';
import { Icon } from './Icon';
import { Command } from '../pages/Command';
import { Solicitations } from '../pages/Solicitations';
import { Clients } from '../pages/Clients';
import { ClientPage } from '../pages/ClientPage';
import { OpportunityPage } from '../pages/OpportunityPage';
import { Budget } from '../pages/Budget';
import { MissionNeeds } from '../pages/MissionNeeds';
import { Ecosystem } from '../pages/Ecosystem';
import { Events } from '../pages/Events';
import { Admin } from '../pages/Admin';
import { Placeholder } from '../pages/Placeholder';
import { TransitionPage } from '../pages/TransitionPage';
import { TechnologyPage } from '../pages/TechnologyPage';

const NAV = [
  { id: 'command', label: 'Command Center', group: 'Overview' },
  { id: 'solicitations', label: 'Solicitations', group: 'Intelligence' },
  { id: 'budget', label: 'Budget & Appropriations', group: 'Intelligence' },
  { id: 'programs', label: 'Programs & Initiatives', group: 'Intelligence' },
  { id: 'mission-needs', label: 'Mission Needs', group: 'Intelligence' },
  { id: 'events', label: 'Events', group: 'Intelligence' },
  { id: 'clients', label: 'Client Profiles', group: 'Accounts' },
  { id: 'ecosystem', label: 'Ecosystem Map', group: 'Accounts' },
  { id: 'analyst', label: 'Digital Analyst', group: 'AI' },
  { id: 'sources', label: 'Data & Ingestion', group: 'Platform' },
  { id: 'admin', label: 'Admin', group: 'Platform' },
];
const GROUPS = NAV.reduce<{ name: string; items: typeof NAV }[]>((acc, n) => {
  const g = acc.find((x) => x.name === n.group);
  if (g) g.items.push(n); else acc.push({ name: n.group, items: [n] });
  return acc;
}, []);

export function Shell({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const { route, params } = useHashRoute();
  const [q, setQ] = useState('');
  const [alertCount, setAlertCount] = useState<number | null>(null);

  // Refresh the bell whenever the route changes; cheap (one row) and keeps it honest after decisions/acks.
  const routeKey = `${route}/${params.join('/')}`;
  useEffect(() => {
    api<{ open: number }>('/api/alerts?limit=1').then((r) => setAlertCount(r.open)).catch(() => setAlertCount(null));
  }, [routeKey]);
  // A stale or foreign hash (old bookmark, NTN prototype route) lands on the Command Center instead of a dead end.
  const known = NAV.some((n) => n.id === route);
  useEffect(() => { if (!known) navigate('command'); }, [known]);

  let page;
  switch (route) {
    case 'command': page = <Command me={me} />; break;
    case 'solicitations': page = <Solicitations params={params} me={me} />; break;
    case 'clients':
      page = params[0] && params[1] === 'opportunity' && params[2] ? <OpportunityPage clientId={params[0]} matchId={params[2]} me={me} />
        : params[0] && params[1] === 'technology' && params[2] ? <TechnologyPage clientId={params[0]} techId={params[2]} me={me} />
        : params[0] && params[1] === 'transition' ? <TransitionPage clientId={params[0]} me={me} />
        : params[0] ? <ClientPage id={params[0]} me={me} /> : <Clients me={me} />;
      break;
    case 'budget': page = <Budget params={params} me={me} />; break;
    case 'mission-needs': page = <MissionNeeds params={params} me={me} />; break;
    case 'ecosystem': page = <Ecosystem params={params} me={me} />; break;
    case 'events': page = <Events params={params} me={me} />; break;
    case 'admin': page = <Admin me={me} />; break;
    default: page = known ? <Placeholder id={route} /> : <Command me={me} />;
  }

  return (
    <>
      <div className="classbar">PRAXIS · {me.tenant.name.toUpperCase()} WORKSPACE · v0.8 · CLIENT OPPORTUNITY MONITORING AND TECHNOLOGY TRANSITION · REAL SAM.GOV, COMPTROLLER AND ECOSYSTEM DATA</div>
      <div className="app">
        <aside className="sidebar">
          <div className="brand">
            <div className="brand-mark">PX</div>
            <div className="brand-text">
              <div className="brand-title">PRAXIS</div>
              <div className="brand-sub">Market &amp; Opportunity Intelligence</div>
            </div>
          </div>
          <nav className="nav">
            {GROUPS.map((g) => (
              <div className="nav-group" key={g.name}>
                <div className="nav-group-label">{g.name}</div>
                {g.items.map((n) => (
                  <a key={n.id} className={`nav-item ${route === n.id ? 'active' : ''}`} href={`#/${n.id}`}>
                    <Icon name={n.id} /><span>{n.label}</span>
                  </a>
                ))}
              </div>
            ))}
          </nav>
          <div className="nav-footer">
            <div className="client-pill"><span className="dot"></span> {me.tenant.name} · {titleCase(me.role)}</div>
            <div className="powered">Gravel Road · Praxis</div>
          </div>
        </aside>
        <main className="main">
          <header className="topbar">
            <form className="search" onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate('solicitations', 'q', q.trim()); }}>
              <Icon name="search" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search solicitations by title or number…" autoComplete="off" />
            </form>
            <div className="topbar-right">
              <a className="bell" href="#/command" title={alertCount === null ? 'Alerts' : `${alertCount} open alert${alertCount === 1 ? '' : 's'}`}>
                <Icon name="bell" />
                {alertCount !== null && alertCount > 0 && <span className="bell-count">{alertCount > 99 ? '99+' : alertCount}</span>}
              </a>
              <a className="avatar" href="#/admin" title={`${me.user.email} · account settings`}>{initials(me.user.display_name || me.user.email)}</a>
              <button className="ghost-btn" onClick={onLogout}>Sign out</button>
            </div>
          </header>
          <div className="view">{page}</div>
        </main>
      </div>
    </>
  );
}
